import {
  Box,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ConfirmDialog,
  DialogLayout,
  NewConvergeButton,
} from '../../../presentation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  usePackageQuoteMutation,
  useUndoQuotePackageMutation,
} from '@converge/graphql';

import { DocumentButton } from '../submission-action/document-button';
import { ISubmissionProps } from '../overview';
import { convergeLogger } from '@converge/config';
import { getQuoteValidationErrors } from './underwriting.quote.grid';
import { useConvergeSnackBar } from '../../../contexts';
import { useOutletContext } from 'react-router-dom';
import { useQuoteListStateContext } from './quote-list.context';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

export const UnderwritingQuotePackageCreateButton = () => {
  const { t } = useTranslation();
  const [, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { navigate: navigatePage } = useUnderwritingSubmissionDetailPage();

  const { packageQuote, loading, error } = usePackageQuoteMutation();

  const { submission: submissionInfo } = useOutletContext<ISubmissionProps>();
  const { selectedQuotesUuids } = useQuoteListStateContext();
  const { notify } = useConvergeSnackBar();
  const {
    submission,
    info: {
      allowCreateQuotePackage,
      hasQuotePackageBeenCreated,
      submissionType,
    },
  } = useUnderwritingSubmissionStateContext();

  const createQuotePackage = useCallback(async () => {
    try {
      convergeLogger.log('Creating quote package...');
      notify({
        message: 'Creating quote package...',
        variant: 'info',
      });
      await packageQuote({
        variables: {
          submissionUuid: submission.uuid,
          quoteUuids: selectedQuotesUuids,
        },
      });
      notify({
        message: 'Quote package created successfully',
        variant: 'success',
      });
      convergeLogger.log('Quote package created.');
      setShowSuccessDialog(true);
      setShowErrorDialog(false);
    } catch (cause) {
      setShowSuccessDialog(false);
      setShowErrorDialog(true);
    }
  }, [packageQuote, selectedQuotesUuids, submission.uuid, notify]);

  const handleCreateQuotePackage = useCallback(async () => {
    setShowConfirmDialog(false);

    await createQuotePackage();
  }, [createQuotePackage]);

  const { quotes } = useQuoteListStateContext();

  const { hasValidationErrors } = useMemo(() => {
    const validationErrors = quotes
      .filter((quote) => selectedQuotesUuids.includes(quote.uuid))
      .reduce<Record<string, string[]>>(
        (acc, quote) => ({
          ...acc,
          [quote.uuid]: getQuoteValidationErrors(quote, submissionType, true),
        }),
        {} as Record<string, string[]>
      );

    return {
      validationErrors,
      hasValidationErrors: Object.values(validationErrors).some(
        (errors) => errors.length > 0
      ),
    };
  }, [quotes, selectedQuotesUuids, submissionType]);

  return (
    <>
      <DialogLayout
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        header={<DialogTitle>Failed to create quote package</DialogTitle>}
      >
        <DialogContent>
          <DialogContentText>
            {error?.message ?? 'Unknown error'}
          </DialogContentText>
        </DialogContent>
      </DialogLayout>

      {allowCreateQuotePackage ? (
        <Box className="button-box">
          <NewConvergeButton
            disabled={
              !selectedQuotesUuids.length || loading || hasValidationErrors
            }
            label={
              hasValidationErrors ? 'Remove invalid quotes' : t('Create quote')
            }
            startIcon={''}
            loading={loading}
            onClick={() => setShowConfirmDialog(true)}
          />
        </Box>
      ) : hasQuotePackageBeenCreated ? (
        <Box className="button-box first-button-box">
          <NewConvergeButton
            // TODO: Hide quote package button with no quote options
            label="View Quote"
            startIcon={''}
            onClick={() => {
              navigatePage.page({
                sectionUrl: `quote/quote-package`,
              });
            }}
          />
        </Box>
      ) : null}
      {submissionInfo?.documentLink && (
        <Box className="button-box">
          <DocumentButton
            submissionDocumentLink={submissionInfo.documentLink}
          />
        </Box>
      )}

      <ConfirmDialog
        show={showConfirmDialog}
        title={`Quote package`}
        confirmButtonText="Yes"
        cancelButtonText="No"
        content={`Are you sure you want to add the selected ${
          selectedQuotesUuids.length > 1
            ? `${selectedQuotesUuids.length} quotes`
            : 'quote'
        } to the quote package?`}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleCreateQuotePackage}
      />
    </>
  );
};

const UndoButton = () => {
  const {
    info: { quotedQuotes },
    submission,
  } = useUnderwritingSubmissionStateContext();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { notify } = useConvergeSnackBar();

  const { undoQuotePackage, loading, error } = useUndoQuotePackageMutation();

  useEffect(() => {
    if (error) {
      notify({
        message: `Failed to undo quote package: ${
          error?.message ?? 'Unknown error'
        }`,
        variant: 'error',
      });
    }
  }, [error, notify]);

  const handleCreateQuotePackage = useCallback(async () => {
    setShowConfirmDialog(false);

    try {
      await undoQuotePackage({
        variables: {
          submissionUuid: submission.uuid,
        },
      });
    } catch {
      notify('Quote package created successfully', {
        variant: 'success',
      });
    }
  }, [notify, submission.uuid, undoQuotePackage]);

  return (
    <>
      <Box className="button-box">
        <NewConvergeButton
          startIcon={''}
          label={quotedQuotes.length === 0 ? 'No quoted quotes' : 'Undo quote'}
          disabled={quotedQuotes.length === 0}
          loading={loading}
          onClick={() => setShowConfirmDialog(true)}
        />
      </Box>
      <ConfirmDialog
        show={showConfirmDialog}
        title={`Undo quote package`}
        confirmButtonText="Yes"
        cancelButtonText="No"
        content={`Are you sure you want to undo the quote package?`}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleCreateQuotePackage}
      />
    </>
  );
};

UnderwritingQuotePackageCreateButton.UndoButton = UndoButton;
